/**
 * feat-305 — conversation state for the assistant panel (ADR 0001 §10.5).
 *
 * A reducer-based hook keyed by the context key (`page:subject`, the same key
 * the backend computes: `server_performance_history:${server_id}`).
 *
 *  - Restore: the first time the hook is `active` for a key it calls
 *    `getCurrentConversation(ctx)`. A different key (a different server) resets
 *    and loads fresh. The owner (AssistantLauncher) stays mounted while the
 *    panel opens and closes, so reopening within a visit doesn't refetch.
 *  - send(text): optimistic user message + an empty `streaming` assistant
 *    message, then `streamChat`. Events fold into that message.
 *  - stop(): abort; the partial text is kept and marked `stopped` (the server
 *    persists the same thing, ADR §1.3).
 *  - retry(): re-sends the failed turn's question as a NEW server turn (ADR Q1)
 *    while locally replacing the failed answer instead of repeating the
 *    question (NOTES §4). See FRONTEND-QUESTION.md Q3.
 *  - newConversation(): clears locally; the next send has conversation_id null.
 *
 * `status`: 'loading' (restoring), 'idle', 'streaming', or 'error' — the last
 * one means the RESTORE failed (NOTES `02c`); an answer error leaves status
 * 'idle' with `error` set and the failed message marked error/blocked.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { getCurrentConversation, streamChat } from '../../services/assistantService';
import type {
  AssistantConversation,
  AssistantError,
  AssistantEvent,
  AssistantMessage,
  PageContext,
} from './types';
import { isAssistantError } from './types';

export function contextKeyOf(ctx: PageContext): string {
  return `${ctx.page}:${ctx.server_id}`;
}

export interface AssistantState {
  key: string | null;
  status: 'loading' | 'idle' | 'streaming' | 'error';
  conversationId: string | null;
  messages: AssistantMessage[];
  workingLabel: string | null;
  error: AssistantError | null;
  restoreError: AssistantError | null;
  lastQuestion: string | null;
  /** `updated_at` of a restored conversation (drives the "from Sep 30" divider). */
  restoredAt: string | null;
  /** Bumped on every finished turn: lets the panel announce once. */
  turnSeq: number;
  /** How the most recent turn ended. */
  lastOutcome: 'done' | 'stopped' | 'error' | null;
  loaded: boolean;
}

const initial = (key: string | null): AssistantState => ({
  key,
  status: 'idle',
  conversationId: null,
  messages: [],
  workingLabel: null,
  error: null,
  restoreError: null,
  lastQuestion: null,
  restoredAt: null,
  turnSeq: 0,
  lastOutcome: null,
  loaded: false,
});

type Action =
  | { type: 'reset'; key: string | null }
  | { type: 'restore_start' }
  | { type: 'restore_ok'; convo: AssistantConversation | null }
  | { type: 'restore_fail'; error: AssistantError }
  | { type: 'send'; text: string; userId: string; asstId: string; replace: boolean; turn: number }
  | { type: 'event'; turn: number; event: AssistantEvent }
  | { type: 'fail'; turn: number; error: AssistantError }
  | { type: 'stop'; turn: number }
  | { type: 'new' };

const nowIso = () => new Date().toISOString();

const isFailed = (m: AssistantMessage | undefined) =>
  !!m && m.role === 'assistant' && (m.status === 'error' || m.status === 'blocked');

function updateLast(
  messages: AssistantMessage[],
  fn: (m: AssistantMessage) => AssistantMessage,
): AssistantMessage[] {
  const i = messages.length - 1;
  if (i < 0 || messages[i].role !== 'assistant') return messages;
  const copy = messages.slice();
  copy[i] = fn(copy[i]);
  return copy;
}

function reducer(turnRef: { current: number }) {
  return (s: AssistantState, a: Action): AssistantState => {
    // Events from a turn that is no longer current (stopped, reset) are dropped.
    if ((a.type === 'event' || a.type === 'fail' || a.type === 'stop') && a.turn !== turnRef.current) {
      return s;
    }
    switch (a.type) {
      case 'reset':
        return initial(a.key);
      case 'restore_start':
        return { ...s, status: 'loading', restoreError: null };
      case 'restore_ok': {
        const c = a.convo;
        return {
          ...s,
          status: 'idle',
          loaded: true,
          conversationId: c ? c.id : null,
          messages: c ? c.messages : [],
          restoredAt: c && c.messages.length ? c.updated_at : null,
          restoreError: null,
        };
      }
      case 'restore_fail':
        return { ...s, status: 'error', restoreError: a.error };
      case 'send': {
        let messages = s.messages;
        if (a.replace) {
          if (isFailed(messages[messages.length - 1])) messages = messages.slice(0, -1);
        } else {
          messages = [
            ...messages,
            {
              id: a.userId,
              role: 'user',
              body: a.text,
              status: 'complete',
              error_code: null,
              footnotes: [],
              created_at: nowIso(),
            },
          ];
        }
        messages = [
          ...messages,
          {
            id: a.asstId,
            role: 'assistant',
            body: '',
            status: 'streaming',
            error_code: null,
            footnotes: [],
            created_at: nowIso(),
          },
        ];
        return {
          ...s,
          status: 'streaming',
          loaded: true,
          messages,
          workingLabel: null,
          error: null,
          restoreError: null,
          lastQuestion: a.text,
          lastOutcome: null,
        };
      }
      case 'event': {
        const e = a.event;
        switch (e.type) {
          case 'start': {
            const messages = s.messages.slice();
            const ai = messages.length - 1;
            if (ai >= 0) messages[ai] = { ...messages[ai], id: e.assistant_message_id };
            // The user message is the one right before the in-flight answer.
            const ui = ai - 1;
            if (ui >= 0 && messages[ui].role === 'user' && messages[ui].id.startsWith('local-')) {
              messages[ui] = { ...messages[ui], id: e.user_message_id };
            }
            return { ...s, conversationId: e.conversation_id, messages };
          }
          case 'status':
            return { ...s, workingLabel: e.phase === 'looking_up' && e.label ? e.label : null };
          case 'footnote': {
            const f = { n: e.n, kind: e.kind, ref_id: e.ref_id, label: e.label };
            return {
              ...s,
              messages: updateLast(s.messages, (m) =>
                m.footnotes.some((x) => x.n === f.n) ? m : { ...m, footnotes: [...m.footnotes, f] },
              ),
            };
          }
          case 'text':
            return {
              ...s,
              workingLabel: null,
              messages: updateLast(s.messages, (m) => ({ ...m, body: m.body + e.delta })),
            };
          case 'done':
            return {
              ...s,
              status: 'idle',
              workingLabel: null,
              turnSeq: s.turnSeq + 1,
              lastOutcome: 'done',
              messages: updateLast(s.messages, (m) => ({
                ...m,
                status: 'complete',
                footnotes: e.footnotes && e.footnotes.length ? e.footnotes : m.footnotes,
              })),
            };
          case 'error':
            return {
              ...s,
              status: 'idle',
              workingLabel: null,
              turnSeq: s.turnSeq + 1,
              lastOutcome: 'error',
              error: {
                code: e.code,
                message: e.message,
                retryable: e.retryable,
                retryAfterS: e.retry_after_s,
              },
              messages: updateLast(s.messages, (m) => ({
                ...m,
                status: e.code === 'privacy_blocked' ? 'blocked' : 'error',
                error_code: e.code,
              })),
            };
          default:
            return s;
        }
      }
      case 'fail':
        return {
          ...s,
          status: 'idle',
          workingLabel: null,
          turnSeq: s.turnSeq + 1,
          lastOutcome: 'error',
          error: a.error,
          messages: updateLast(s.messages, (m) =>
            m.status === 'streaming' ? { ...m, status: 'error', error_code: a.error.code } : m,
          ),
        };
      case 'stop':
        return {
          ...s,
          status: 'idle',
          workingLabel: null,
          turnSeq: s.turnSeq + 1,
          lastOutcome: 'stopped',
          messages: updateLast(s.messages, (m) =>
            m.status === 'streaming' ? { ...m, status: 'stopped' } : m,
          ),
        };
      case 'new':
        return { ...initial(s.key), loaded: true };
      default:
        return s;
    }
  };
}

const toAssistantError = (e: unknown): AssistantError =>
  isAssistantError(e)
    ? e
    : { code: 'internal', message: 'Unexpected error', retryable: true, retryAfterS: null };

let localSeq = 0;
const localId = (p: string) => `local-${p}-${(localSeq += 1)}`;

export interface UseAssistantConversation {
  state: AssistantState;
  send: (text: string) => void;
  stop: () => void;
  retry: () => void;
  newConversation: () => void;
  reloadRestore: () => void;
  /** True when sending is currently allowed (not streaming, not loading, not full, not disabled). */
  canSend: boolean;
}

/**
 * @param ctx    the page context, or null when the page can't build one.
 * @param active load (restore) only once true, e.g. the first time the panel opens.
 */
export function useAssistantConversation(
  ctx: PageContext | null,
  active: boolean,
): UseAssistantConversation {
  const key = ctx ? contextKeyOf(ctx) : null;
  const turnRef = useRef(0);
  const reducerRef = useRef(reducer(turnRef));
  const [state, dispatch] = useReducer(reducerRef.current, key, initial);
  const ctrlRef = useRef<AbortController | null>(null);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const stateRef = useRef(state);
  stateRef.current = state;
  const loadSeq = useRef(0);

  const abortInFlight = useCallback(() => {
    if (ctrlRef.current) {
      ctrlRef.current.abort();
      ctrlRef.current = null;
    }
  }, []);

  // A different subject (server) → a fresh conversation state.
  useEffect(() => {
    if (state.key !== key) {
      abortInFlight();
      turnRef.current += 1;
      loadSeq.current += 1;
      dispatch({ type: 'reset', key });
    }
  }, [key, state.key, abortInFlight]);

  const load = useCallback(() => {
    const c = ctxRef.current;
    if (!c) return;
    const seq = (loadSeq.current += 1);
    dispatch({ type: 'restore_start' });
    getCurrentConversation(c).then(
      (convo) => {
        if (seq === loadSeq.current) dispatch({ type: 'restore_ok', convo });
      },
      (err) => {
        if (seq === loadSeq.current) dispatch({ type: 'restore_fail', error: toAssistantError(err) });
      },
    );
  }, []);

  useEffect(() => {
    if (active && key && state.key === key && !state.loaded && state.status === 'idle') {
      load();
    }
  }, [active, key, state.key, state.loaded, state.status, load]);

  // Unmount (navigating away) aborts the in-flight answer.
  useEffect(() => () => abortInFlight(), [abortInFlight]);

  const run = useCallback(
    (text: string, replace: boolean) => {
      const c = ctxRef.current;
      if (!c) return;
      abortInFlight();
      turnRef.current += 1;
      loadSeq.current += 1; // a send supersedes any pending restore
      const turn = turnRef.current;
      const controller = new AbortController();
      ctrlRef.current = controller;
      dispatch({ type: 'send', text, userId: localId('u'), asstId: localId('a'), replace, turn });
      streamChat(
        { conversation_id: stateRef.current.conversationId, page_context: c, message: text },
        (event) => dispatch({ type: 'event', turn, event }),
        controller.signal,
      ).then(
        () => {
          if (ctrlRef.current === controller) ctrlRef.current = null;
        },
        (err) => {
          if (ctrlRef.current === controller) ctrlRef.current = null;
          if (!controller.signal.aborted) dispatch({ type: 'fail', turn, error: toAssistantError(err) });
        },
      );
    },
    [abortInFlight],
  );

  // While a key change is pending its reset, never show the previous subject's messages.
  const s = state.key === key ? state : initial(key);
  const full = s.error?.code === 'limit_conversation';
  const disabled = s.error?.code === 'not_found' || s.restoreError?.code === 'not_found';
  const canSend = !!ctx && s.status !== 'streaming' && s.status !== 'loading' && !full && !disabled;

  const send = useCallback(
    (text: string) => {
      const t = text.trim();
      if (!t) return;
      const cur = stateRef.current;
      if (cur.status === 'streaming' || cur.status === 'loading') return;
      if (cur.error?.code === 'limit_conversation' || cur.error?.code === 'not_found') return;
      run(t, false);
    },
    [run],
  );

  const stop = useCallback(() => {
    if (stateRef.current.status !== 'streaming') return;
    abortInFlight();
    // Advance the turn so any late event from the stopped stream is dropped by the reducer.
    turnRef.current += 1;
    dispatch({ type: 'stop', turn: turnRef.current });
  }, [abortInFlight]);

  const retry = useCallback(() => {
    const cur = stateRef.current;
    if (cur.status === 'streaming' || cur.status === 'loading') return;
    const msgs = cur.messages;
    const last = msgs[msgs.length - 1];
    if (!isFailed(last)) return;
    const q = [...msgs].reverse().find((m) => m.role === 'user');
    if (!q) return;
    run(q.body, true);
  }, [run]);

  const newConversation = useCallback(() => {
    if (stateRef.current.status === 'streaming') return;
    abortInFlight();
    turnRef.current += 1;
    loadSeq.current += 1;
    dispatch({ type: 'new' });
  }, [abortInFlight]);

  const reloadRestore = useCallback(() => load(), [load]);

  return { state: s, send, stop, retry, newConversation, reloadRestore, canSend };
}
